/**
 * KCIS™ Pro — Practitioner Review status bar (Sprint 6B4).
 *
 * Shows the current review status for an assessment and offers a single
 * action to flip it (reviewed / approved / requires_follow_up).
 *
 * Flag-gated server-side by KCIS_PRACTITIONER_OVERRIDE_ENABLED — the bar
 * still renders the status (read-only). Action button surfaces a 503 toast
 * if the flag is off and silently disables itself.
 */
import { useState } from "react";
import { CheckCircle2, ClipboardCheck, AlertCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

const STATUS_TOKENS = {
  reviewed: {
    label: "Reviewed",
    Icon: ClipboardCheck,
    chipClass: "bg-slate-100 text-slate-800 border-slate-200",
  },
  approved: {
    label: "Approved",
    Icon: CheckCircle2,
    chipClass: "bg-emerald-50 text-emerald-800 border-emerald-200",
  },
  requires_follow_up: {
    label: "Requires follow-up",
    Icon: AlertCircle,
    chipClass: "bg-amber-50 text-amber-900 border-amber-200",
  },
};

const NEXT_OPTIONS = [
  { value: "reviewed", label: "Mark reviewed" },
  { value: "approved", label: "Approve" },
  { value: "requires_follow_up", label: "Needs follow-up" },
];

export default function ReviewStatusBar({ assessmentId, status, onChanged }) {
  const [submitting, setSubmitting] = useState(null);
  const current = status?.status;
  const token = current ? STATUS_TOKENS[current] : null;
  const Icon = token?.Icon;

  const mark = async (next) => {
    setSubmitting(next);
    try {
      const r = await api.post(`/nutrient/${assessmentId}/review/mark`, { status: next });
      if (r?.data?.ok) {
        toast.success(`Marked as ${STATUS_TOKENS[next].label.toLowerCase()}`);
        onChanged?.();
      } else {
        toast.error(r?.data?.error || "Could not update status");
      }
    } catch (err) {
      if (err?.response?.status === 503) {
        toast.message("Practitioner review actions are temporarily disabled.");
      } else {
        toast.error("Could not update review status");
      }
    } finally {
      setSubmitting(null);
    }
  };

  return (
    <div
      data-testid="practitioner-review-status-bar"
      className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5 px-4 py-3 rounded-2xl border border-slate-200 bg-white"
    >
      <div className="flex items-center gap-3 min-w-0">
        <span className="kcis-overline text-slate-500">Review status</span>
        {current && token ? (
          <span
            data-testid={`review-status-chip-${current}`}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs border ${token.chipClass}`}
          >
            {Icon ? <Icon className="w-3.5 h-3.5" /> : null}
            {token.label}
          </span>
        ) : (
          <span
            data-testid="review-status-chip-none"
            className="inline-flex items-center px-2.5 py-1 rounded-full text-xs border bg-slate-50 text-slate-500 border-slate-200"
          >
            Not yet reviewed
          </span>
        )}
        {status?.marked_at ? (
          <span className="text-xs text-slate-500 truncate">
            · last marked {new Date(status.marked_at).toLocaleString()} by {status.marked_by || "—"}
          </span>
        ) : null}
      </div>
      <div className="sm:ml-auto flex flex-wrap gap-2">
        {NEXT_OPTIONS.map((opt) => (
          <Button
            key={opt.value}
            data-testid={`review-status-mark-${opt.value}`}
            size="sm"
            variant={current === opt.value ? "default" : "outline"}
            disabled={submitting !== null}
            onClick={() => mark(opt.value)}
          >
            {submitting === opt.value ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              opt.label
            )}
          </Button>
        ))}
      </div>
    </div>
  );
}
